import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import {
  selectAverageData,
  selectProfitCalculator,
} from "../../../../../features/productAnalyzer/productAnalyzer.selector";
import {
  formatCurrency,
  formatPercent,
  toNumber,
} from "../../../../../helpers";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { updateLookupThunk } from "../../../../../features/productAnalyzer/productAnalyzer.thunk";
import Calculator from "../../../../../helpers/profitCalculator";
import { useEffect, useState, useRef } from "react";
import ProfitCalculator from "../ProfitCalculator";
import FulfilmentSelector from "../../common/FulfilmentSelector";
import PriceInput from "../../common/PriceInput";
import PriceBadges from "../../common/PriceBadges";
import MetricPanel from "../../common/MetricPanel";
import Panel from "../../common/Panel";
import { colors } from "../../config";

export default function QuickInfo() {
  const [percentChange, setPercentChange] = useState<number | null>(null);
  const [isPricingPanelOpen, setIsPricingPanelOpen] = useState(false);
  const priceInfoIconRef = useRef<HTMLElement | null>(null);
  const dispatch = useAppDispatch();
  const { profitCalculator, feeRules } = useSelector(selectProfitCalculator);
  const { averages } = useAppSelector(selectAverageData);

  const handleCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      dispatch(updateLookupThunk({ costPrice: "" as any }));
      return;
    }
    dispatch(updateLookupThunk({ costPrice: toNumber(value) }));
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      dispatch(updateLookupThunk({ salePrice: "" as any }));
      return;
    }
    dispatch(updateLookupThunk({ salePrice: toNumber(value) }));
  };

  const BBPrice = averages?.buyBox?.avgCurrent;
  const avgBBPrice = averages?.buyBox?.avg90;
  const avgFBAPrice = averages?.lowestFBA?.Current?.average;
  const avgFBMPrice = averages?.lowestFBM?.Current?.average;
  const {
    profit,
    margin,
    roi,
    maxCost,
    breakevenSalePrice,
    totalFee,
    payout,
    prepFee,
    miscFeeAmount,
    miscFeePercentAmount,
    storageFee,
    inboundPlacementFee,
    referralFee,
    referralPercent,
    fbaFee,
  } = new Calculator(profitCalculator, feeRules).calculate();
  const salePrice: number = toNumber(profitCalculator.salePrice);
  const costPrice: number = toNumber(profitCalculator.costPrice);
  const inboundShipping = toNumber(profitCalculator.inboundShipping);
  const additionalExpenses =
    prepFee +
    miscFeeAmount +
    miscFeePercentAmount +
    (profitCalculator.fulfilmentType === "FBA" ? inboundShipping : 0);
  const fulfilmentFee =
    profitCalculator.fulfilmentType === "FBA"
      ? fbaFee
      : toNumber(profitCalculator.fbmCost);

  const closingFee = toNumber(profitCalculator.closingFee);

  const totalCost = costPrice * profitCalculator.quantity;
  const totalSale = salePrice * profitCalculator.quantity;
  const totalProfit = profit * profitCalculator.quantity;

  useEffect(() => {
    if (
      avgBBPrice != null &&
      avgBBPrice > 0 &&
      profitCalculator.salePrice &&
      profitCalculator.salePrice !== avgBBPrice
    ) {
      const current = toNumber(profitCalculator.salePrice);
      const pct = ((current - avgBBPrice) / avgBBPrice) * 100;
      setPercentChange(pct);
    } else {
      setPercentChange(null);
    }
  }, [profitCalculator.salePrice, avgBBPrice]);

  const handlePriceBadgeClick = (value: number) => {
    dispatch(updateLookupThunk({ salePrice: value }));
    if (avgBBPrice != null && value !== avgBBPrice) {
      setPercentChange(((value - avgBBPrice) / avgBBPrice) * 100);
    } else {
      setPercentChange(null);
    }
  };

  const handleFulfilmentSelect = (type: "FBA" | "FBM") => {
    dispatch(updateLookupThunk({ fulfilmentType: type }));
  };

  const handleInfoIconClick = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    // Get the parent element (the InputAdornment) as the anchor
    const parentElement = e.currentTarget.closest(".MuiInputAdornment-root") as HTMLElement;
    priceInfoIconRef.current = parentElement || (e.currentTarget as unknown as HTMLElement);
    // Toggle panel: close if already open, open if closed
    setIsPricingPanelOpen((prev) => !prev);
  };

  const handlePriceSelect = (price: number) => {
    dispatch(updateLookupThunk({ salePrice: Number(price.toFixed(2)) }));
    // Don't close the panel - let user select other values
    if (avgBBPrice != null && price !== avgBBPrice) {
      setPercentChange(((price - avgBBPrice) / avgBBPrice) * 100);
    } else {
      setPercentChange(null);
    }
  };

  // Close panel when clicking outside (but not when clicking on the panel itself or the icon)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isPricingPanelOpen) return;

      const target = event.target as Node;
      const panelElement = document.querySelector('[data-pricing-panel]');
      const iconElement = priceInfoIconRef.current;

      // Don't close if clicking on the panel itself or the icon
      if (
        panelElement?.contains(target) ||
        (iconElement && iconElement.contains(target))
      ) {
        return;
      }

      setIsPricingPanelOpen(false);
    };

    if (isPricingPanelOpen) {
      // Use a small delay to avoid closing immediately when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isPricingPanelOpen]);

  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 1.25,
        borderRadius: "8px",
        background: colors.background.lightGray,
        px: 1,
        py: 0.75,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <FulfilmentSelector
        currentType={profitCalculator.fulfilmentType}
        onSelect={handleFulfilmentSelect}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "left",
          mb: 1,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <PriceInput
          id="price"
          label="Price"
          value={profitCalculator.salePrice}
          onChange={handlePriceChange}
          percentChange={percentChange}
          onInfoIconClick={handleInfoIconClick}
        />
        <PriceInput
          id="cost"
          label="Cost"
          value={profitCalculator.costPrice}
          onChange={handleCostChange}
        />
      </Box>
      <PriceBadges
        buyBoxPrice={BBPrice}
        avgFBMPrice={avgFBMPrice}
        avgFBAPrice={avgFBAPrice}
        avgBBPrice={avgBBPrice}
        onPriceSelect={handlePriceBadgeClick}
      />
      <Box
        sx={{
          background: colors.background.white,
          py: 0.75,
          px: 0.75,
          mb: 1.375,
        }}
      >
        <Box display="flex" gap={0.75} mb={0.75}>
          <MetricPanel
            title="Profit"
            variant={
              profit >= toNumber(profitCalculator.minProfit)
                ? "success"
                : "fail"
            }
          >
            {formatCurrency(profit)}
          </MetricPanel>
          <MetricPanel
            title="ROI"
            variant={
              typeof roi === "undefined" || roi >= profitCalculator.minRoi
                ? "success"
                : "fail"
            }
          >
            {formatPercent(roi)}
          </MetricPanel>
          <MetricPanel
            title="Margin"
            variant={
              profit >= toNumber(profitCalculator.minProfit)
                ? "success"
                : "fail"
            }
          >
            {formatPercent(margin)}
          </MetricPanel>
        </Box>
        <Box display="flex" gap={0.75}>
          <MetricPanel
            title="Max Cost"
            variant={
              costPrice === 0
                ? "info"
                : !!maxCost && costPrice <= maxCost
                  ? "success"
                  : "fail"
            }
          >
            {formatCurrency(maxCost)}
          </MetricPanel>
          <MetricPanel
            title="Breakeven"
            variant={salePrice >= breakevenSalePrice ? "success" : "fail"}
          >
            {formatCurrency(breakevenSalePrice)}
          </MetricPanel>
        </Box>
      </Box>
      <ProfitCalculator
        calculationData={{
          totalFee,
          additionalExpenses,
          discountPercent: profitCalculator.totalDiscount,
          breakevenPrice: breakevenSalePrice,
          amazonPayout: payout,
          quantity: profitCalculator.quantity,
          referralFee,
          referralPercent,
          fbaFee,
          inboundPlacementFee,
          storageFee,
          inboundShipping,
          prepFee,
          miscFeeAmount,
          miscFeePercent: profitCalculator.miscFeePercent,
          miscFeePercentAmount,
          salePrice,
          fulfilmentFee,
          closingFee,
          totalCost,
          totalSale,
          totalProfit,
          fulfilmentType: profitCalculator.fulfilmentType,
          inboundPlacementService: profitCalculator.inboundPlacementService,
        }}
        onQuantityChange={(quantity) => {
          dispatch(updateLookupThunk({ quantity }));
        }}
        onInboundPlacementChange={(service) => {
          dispatch(updateLookupThunk({ inboundPlacementService: service }));
        }}
        onPrepFeeChange={(prepFee) => {
          dispatch(updateLookupThunk({ prepFee }));
        }}
        onInboundShippingChange={(inboundShipping) => {
          dispatch(updateLookupThunk({ inboundShipping }));
        }}
        onMiscFeeChange={(miscFee) => {
          dispatch(updateLookupThunk({ miscFee }));
        }}
        onMiscFeePercentChange={(miscFeePercent) => {
          dispatch(updateLookupThunk({ miscFeePercent }));
        }}
      />
      <Panel
        isOpen={isPricingPanelOpen}
        anchorElement={priceInfoIconRef.current}
        type="pricing"
        pricingData={{
          buyBoxPrice: BBPrice,
          avgBBPrice: avgBBPrice,
          minFBMPrice: avgFBMPrice,
          minFBAPrice: avgFBAPrice,
        }}
        onPriceSelect={handlePriceSelect}
      />
    </Box>
  );
}
